import { DEFAULT_THEME } from '../utils/theme';
import { DEFAULT_SETTINGS } from '../utils/settings';

const SettingsPanel = ({
  settings,
  updateSetting,
  resetSettings,
  theme,
  themeDraft,
  onThemeChange,
  onThemeCommit,
  onThemeReset,
  updateStatus,
  checkForUpdates,
  handleDownloadUpdate,
  canDownloadUpdate,
  updateAvailable,
  updateDownloadBlocked,
  updateError,
  latestUpdate,
  updateCheckedAt,
  APP_VERSION,
  formatSize,
  isWebMode = false
}) => {
  const normalizeHex = (value) => {
    const match = String(value || '').trim().match(/^#?([0-9a-fA-F]{6})$/);
    return match ? `#${match[1].toUpperCase()}` : null;
  };

  const getSwatchValue = (key) => normalizeHex(themeDraft[key]) || theme[key];

  const renderColorField = (field) =>
  <div
    key={field.key}
    className="rounded-xl border border-border bg-inset/40 p-4 flex flex-col gap-3">
    
      <div>
        <p className="text-sm font-semibold text-textPrimary">{field.label}</p>
        <p className="text-xs text-textMuted">{field.description}</p>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <input
        type="color"
        value={getSwatchValue(field.key)}
        onChange={(e) => onThemeCommit(field.key, e.target.value)}
        className="h-10 w-16 rounded-md border border-border bg-inset/60 p-1" />
      
        <input
        type="text"
        value={themeDraft[field.key] || ''}
        onChange={(e) => onThemeChange(field.key, e.target.value)}
        onBlur={(e) => onThemeCommit(field.key, e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onThemeCommit(field.key, e.currentTarget.value);
            e.currentTarget.blur();
          }
        }}
        className="flex-1 min-w-[180px] rounded-lg border border-border bg-inset/60 px-3 py-2 text-sm text-white placeholder:text-textMuted focus:border-accent focus:outline-none"
        placeholder="#RRGGBB" />
      
      </div>
    </div>;


  const renderSettingField = (field, content) =>
  <div
    key={field.key}
    className="rounded-xl border border-border bg-inset/40 p-4 flex flex-col gap-3">
    
      <div>
        <p className="text-sm font-semibold text-textPrimary">{field.label}</p>
        <p className="text-xs text-textMuted">{field.description}</p>
      </div>
      {content}
    </div>;


  const themeFields = [
  {
    key: 'accent',
    label: 'Main color',
    description: 'Buttons, highlights, and focus rings. A deeper shade is derived automatically.'
  }];

  const backgroundFields = [
  {
    key: 'surface',
    label: 'Surface background',
    description: 'Main window background.'
  },
  {
    key: 'panel',
    label: 'Panel background',
    description: 'Panels and cards.'
  },
  {
    key: 'inset',
    label: 'Inset background',
    description: 'Inputs and list interiors.'
  }];


  const curSettings = settings || DEFAULT_SETTINGS;
  const updateLabel = latestUpdate?.version_number ?
  `v${latestUpdate.version_number}` :
  latestUpdate?.name;

  return (
    <section className="glass-panel rounded-2xl p-6 flex flex-col gap-8 overflow-y-auto flex-1 min-h-0">
      <div>
        <p className="text-xs font-semibold text-textMuted">Settings</p>
        <h2 className="text-lg font-semibold text-white m-0">{isWebMode ? 'Web file settings' : 'Appearance'}</h2>
        <p className="text-sm text-textMuted mt-1">
          {isWebMode ?
          'Controls shown here are supported in web mode.' :
          'Customize the main color and background shades. Changes save automatically.'}
        </p>
        <p className="text-xs text-textMuted mt-2">Version v{APP_VERSION}</p>
      </div>

      {!isWebMode ?
      <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {themeFields.map((field) => renderColorField(field))}
          </div>
          <div>
            <p className="text-xs font-semibold text-textMuted">Backgrounds</p>
            <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-4">
              {backgroundFields.map((field) => renderColorField(field))}
            </div>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-xs text-textMuted">Saved on this device.</p>
            <button type="button" className="ghost-button" onClick={onThemeReset}>
              Reset to default
            </button>
          </div>
        </> :
      null}

      {!isWebMode ?
      <div className="border-t border-border/60 pt-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-base font-semibold text-white m-0">Updates</h3>
              <p className="text-xs text-textMuted mt-1">
                Check for the latest Kinetic SFTP build.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="ghost-button"
                onClick={() => checkForUpdates()}
                disabled={updateStatus === 'checking'}>
                
                {updateStatus === 'checking' ? 'Checking...' : 'Check updates'}
              </button>
              {canDownloadUpdate ?
              <button
                type="button"
                className="pill-button px-4 py-2"
                onClick={handleDownloadUpdate}>
                
                  Download & close
                </button> :
              null}
            </div>
          </div>
          <div className="mt-3 flex flex-col gap-1 text-sm">
            {updateStatus === 'checking' ?
            <span className="text-textMuted">Checking for updates...</span> :
            updateStatus === 'error' ?
            <span className="text-red-300">
                {updateError || 'Update check failed.'}
              </span> :
            updateAvailable ?
            canDownloadUpdate ?
            <span className="text-accent">
                  Update available: {updateLabel} ({'current v' + APP_VERSION})
                </span> :
            updateDownloadBlocked ?
            <span className="text-yellow-300">
                  Update available: {updateLabel} ({'current v' + APP_VERSION}) but the download link is blocked by client policy.
                </span> :
            <span className="text-accent">
                  Update available: {updateLabel} ({'current v' + APP_VERSION})
                </span> :
            updateLabel ?
            <span className="text-textPrimary/80">
                  Latest: {updateLabel} ({'current v' + APP_VERSION})
                </span> :
            <span className="text-textMuted">No update data yet.</span>
            }
            {latestUpdate?.size_bytes ?
            <span className="text-xs text-textMuted">
                Download size {formatSize(latestUpdate.size_bytes)}
              </span> :
            null}
            {latestUpdate?.created_at ?
            <span className="text-xs text-textMuted">
                Published {new Date(latestUpdate.created_at).toLocaleString()}
              </span> :
            null}
            {updateCheckedAt ?
            <span className="text-xs text-textMuted">
                Last checked {new Date(updateCheckedAt).toLocaleString()}
              </span> :
            null}
          </div>
        </div> :
      null}

      <div className="border-t border-border/60 pt-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-white m-0">Transfer limits</h3>
            <p className="text-xs text-textMuted">Use 0 to mean unlimited.</p>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
          {renderSettingField(
            {
              key: 'perFileSizeLimitMb',
              label: 'Size limit (MB)',
              description: 'Skip files larger than this (0 = no limit).'
            },
            <input
              type="number"
              className="w-full rounded-lg border border-border bg-inset/60 px-3 py-2 text-sm text-white focus:border-accent focus:outline-none"
              value={curSettings.transferLimits.perFileSizeLimitMb}
              onChange={(e) =>
              updateSetting('transferLimits', 'perFileSizeLimitMb', e.target.value)
              } />

          )}
        </div>
      </div>

      <div className="border-t border-border/60 pt-6">
        <div>
          <h3 className="text-base font-semibold text-white m-0">{isWebMode ? 'Behavior' : 'Timeouts & behavior'}</h3>
          <p className="text-xs text-textMuted mt-1">
            {isWebMode ? 'Web upload behavior controls.' : 'Network timings and file handling.'}
          </p>
        </div>
        <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
          {!isWebMode ?
          <>
              {renderSettingField(
              {
                key: 'connectionSec',
                label: 'Connect timeout (s)',
                description: 'Max time to wait for initial connection (5-600s).'
              },
              <input
                type="number"
                min={5}
                max={600}
                className="w-full rounded-lg border border-border bg-inset/60 px-3 py-2 text-sm text-white focus:border-accent focus:outline-none"
                value={curSettings.timeouts.connectionSec}
                onChange={(e) =>
                updateSetting('timeouts', 'connectionSec', e.target.value)
                } />

            )}
              {renderSettingField(
              {
                key: 'idleSec',
                label: 'Idle timeout (s)',
                description: 'Disconnect after inactivity (0 = never).'
              },
              <input
                type="number"
                min={0}
                max={3600}
                className="w-full rounded-lg border border-border bg-inset/60 px-3 py-2 text-sm text-white focus:border-accent focus:outline-none"
                value={curSettings.timeouts.idleSec}
                onChange={(e) =>
                updateSetting('timeouts', 'idleSec', e.target.value)
                } />

            )}
              {renderSettingField(
              {
                key: 'overwriteRule',
                label: 'Overwrite rule',
                description: 'Action when a file already exists.'
              },
              <select
                className="w-full rounded-lg border border-border bg-inset/60 px-3 py-2 text-sm text-white focus:border-accent focus:outline-none"
                value={curSettings.transferMode.overwriteRule}
                onChange={(e) =>
                updateSetting('transferMode', 'overwriteRule', e.target.value)
                }>
                
                <option value="prompt">Ask me</option>
                <option value="skip">Skip</option>
                <option value="replace">Overwrite</option>
                <option value="rename">Rename new</option>
              </select>
            )}
            </> :
          null}
          {renderSettingField(
            {
              key: 'dragDrop',
              label: 'Drag & Drop',
              description: 'Behavior when dropping files.'
            },
            <select
              className="w-full rounded-lg border border-border bg-inset/60 px-3 py-2 text-sm text-white focus:border-accent focus:outline-none"
              value={curSettings.uiux.dragDropBehavior}
              onChange={(e) =>
              updateSetting('uiux', 'dragDropBehavior', e.target.value)
              }>
              
              <option value="upload">Upload immediately</option>
              <option value="confirm">Confirm before upload</option>
              <option value="disabled">Disabled</option>
            </select>
          )}
        </div>
      </div>

      <div className="border-t border-border/60 pt-6">
        <div>
          <h3 className="text-base font-semibold text-white m-0">{isWebMode ? 'Visibility & safety' : 'Defaults & paths'}</h3>
          <p className="text-xs text-textMuted mt-1">
            {isWebMode ? 'File list visibility and safety prompts.' : 'Preferred locations and visibility.'}
          </p>
        </div>
        <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
          {!isWebMode ?
          <>
              {renderSettingField(
              {
                key: 'defaultLocalPath',
                label: 'Local path',
                description: 'Start here on launch (blank for home).'
              },
              <input
                type="text"
                className="w-full rounded-lg border border-border bg-inset/60 px-3 py-2 text-sm text-white placeholder:text-textMuted focus:border-accent focus:outline-none"
                value={curSettings.uiux.defaultLocalPath}
                onChange={(e) =>
                updateSetting('uiux', 'defaultLocalPath', e.target.value)
                }
                placeholder="e.g. C:/Users/Name/Documents" />

            )}
              {renderSettingField(
              {
                key: 'defaultRemotePath',
                label: 'Remote path',
                description: 'Start here after connecting (blank for root).'
              },
              <input
                type="text"
                className="w-full rounded-lg border border-border bg-inset/60 px-3 py-2 text-sm text-white placeholder:text-textMuted focus:border-accent focus:outline-none"
                value={curSettings.uiux.defaultRemotePath}
                onChange={(e) =>
                updateSetting('uiux', 'defaultRemotePath', e.target.value)
                }
                placeholder="e.g. /home/user/mc-server" />

            )}
            </> :
          null}
          <div className="col-span-1 lg:col-span-2 flex flex-col gap-3 rounded-xl border border-border bg-inset/40 p-4">
            <p className="text-sm font-semibold text-textPrimary">Toggles</p>
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2 text-sm text-textMuted">
                <input
                  type="checkbox"
                  checked={curSettings.uiux.showHiddenFiles}
                  onChange={(e) =>
                  updateSetting('uiux', 'showHiddenFiles', e.target.checked)
                  } />
                
                Show hidden files
              </label>
              <label className="flex items-center gap-2 text-sm text-textMuted">
                <input
                  type="checkbox"
                  checked={curSettings.uiux.confirmDelete}
                  onChange={(e) =>
                  updateSetting('uiux', 'confirmDelete', e.target.checked)
                  } />
                
                Confirm deletions
              </label>
              <label className="flex items-center gap-2 text-sm text-textMuted">
                <input
                  type="checkbox"
                  checked={curSettings.queueBehavior.pauseOnError}
                  onChange={(e) =>
                  updateSetting('queueBehavior', 'pauseOnError', e.target.checked)
                  } />
                
                Pause queue on error
              </label>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-border/60 pt-6">
        <div>
          <h3 className="text-base font-semibold text-white m-0">{isWebMode ? 'Logging' : 'Advanced'}</h3>
          <p className="text-xs text-textMuted mt-1">{isWebMode ? 'Console history settings.' : 'Logging and performance.'}</p>
        </div>
        <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
          {renderSettingField(
            {
              key: 'logRetention',
              label: 'Log retention',
              description: 'Max lines to keep in console history.'
            },
            <input
              type="number"
              min={0}
              max={2000}
              className="w-full rounded-lg border border-border bg-inset/60 px-3 py-2 text-sm text-white focus:border-accent focus:outline-none"
              value={curSettings.logging.retention}
              onChange={(e) =>
              updateSetting('logging', 'retention', e.target.value)
              } />

          )}
          {!isWebMode ?
          <>
              {renderSettingField(
              {
                key: 'sftpCompression',
                label: 'SFTP Compression',
                description: 'Enable protocol-level compression (if supported).'
              },
              <label className="flex items-center gap-2 text-sm text-textMuted mt-1">
                <input
                  type="checkbox"
                  checked={curSettings.compression.enableSftpCompression}
                  onChange={(e) =>
                  updateSetting('compression', 'enableSftpCompression', e.target.checked)
                  } />
                
                Enabled
              </label>
            )}
            </> :
          null}
        </div>
      </div>

      <div className="border-t border-border/60 pt-6 flex items-center justify-between">
        <p className="text-xs text-textMuted">Preferences saved on this device.</p>
        <button type="button" className="ghost-button" onClick={resetSettings}>
          Reset settings
        </button>
      </div>
    </section>);

};

export default SettingsPanel;
